import React from 'react';
import clsx from 'clsx';
import { Table, Drawer, Input, Tooltip, Spin, Typography, Space, Tag, Button, Modal, Switch } from 'antd';
import type { TableColumnsType } from 'antd';
import { SearchOutlined, CloseOutlined, ArrowRightOutlined, SettingOutlined, VerticalAlignMiddleOutlined } from '@ant-design/icons';
import { Button as AntButton } from 'antd';
import { PipelineDrawerAggregates, PipelineDrawerRow, PipelineSummary } from './types';
import { handleEntityLinkClick } from '../utils';
import { SalesPipelineStage } from '../../../types';
import { env } from '../../../env';

const { Title, Text } = Typography;

const CITY_ROWS_PER_PAGE = 10;
const COMPANY_ROWS_PER_PAGE = 10;

const PIPELINE_STAGE_SEQUENCE: SalesPipelineStage[] = ['ENQUIRY', 'OPPORTUNITY', 'QUOTATION', 'SALES_ORDER', 'INVOICED'];

const STAGE_URL_SUFFIX: Record<SalesPipelineStage, string> = {
  ENQUIRY: '/sales-pipeline?enquiry_flow=enquiries&search=',
  OPPORTUNITY: '/sales-pipeline?enquiry_flow=oppertunity&search=',
  QUOTATION: '/sales-pipeline?enquiry_flow=quotations&search=',
  SALES_ORDER: '/sales-pipeline?enquiry_flow=sales_order&search=',
  INVOICED: '/sales-pipeline?enquiry_flow=invoices&search=',
};

const STAGE_COLORS: Record<SalesPipelineStage, string> = {
  ENQUIRY: '#1d4ed8',
  OPPORTUNITY: '#0f766e',
  QUOTATION: '#f59e0b',
  SALES_ORDER: '#8b5cf6',
  INVOICED: '#16a34a',
};

const STAGE_CARD_BACKGROUNDS: Record<SalesPipelineStage, string> = {
  ENQUIRY: 'linear-gradient(135deg, #eef2ff 0%, #dbeafe 100%)',
  OPPORTUNITY: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
  QUOTATION: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
  SALES_ORDER: 'linear-gradient(135deg, #ede9fe 0%, #e9d5ff 100%)',
  INVOICED: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
};

const STAGE_CARD_LABELS: Record<SalesPipelineStage, string> = {
  ENQUIRY: 'Enquiries',
  OPPORTUNITY: 'Active Opportunities',
  QUOTATION: 'Active Quotations',
  SALES_ORDER: 'Active Sales Orders',
  INVOICED: 'Active Invoices',
};

interface ColumnSettingsModalProps {
  isVisible: boolean;
  onCancel: () => void;
  columns: Record<string, boolean>;
  columnOrder: Record<string, number>;
  onToggle: (key: string) => void;
  onReset: () => void;
  onReorder: (updatedOrder: Record<string, number>) => void;
  requiredColumns?: string[];
  columnDisplayNames: Record<string, string>;
}

const ColumnSettingsModal = React.memo(({
  isVisible,
  onCancel,
  columns,
  columnOrder,
  onToggle,
  onReset,
  onReorder,
  requiredColumns = [],
  columnDisplayNames,
}: ColumnSettingsModalProps) => {
  const [draggedItem, setDraggedItem] = React.useState<string | null>(null);
  const [dragOverItem, setDragOverItem] = React.useState<string | null>(null);

  // Sort columns by their order
  const sortedColumns = Object.entries(columns)
    .sort(([a], [b]) => {
      const orderA = columnOrder[a] || 999;
      const orderB = columnOrder[b] || 999;
      return orderA - orderB;
    });

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, key: string) => {
    setDraggedItem(key);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>, key: string) => {
    e.preventDefault();
    setDragOverItem(key);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (draggedItem && dragOverItem && draggedItem !== dragOverItem) {
      const newOrder = { ...columnOrder };
      
      const allColumns = Object.entries(newOrder).sort(([, a], [, b]) => a - b);
      const draggedIndex = allColumns.findIndex(([key]) => key === draggedItem);
      const dropIndex = allColumns.findIndex(([key]) => key === dragOverItem);

      if (draggedIndex === -1 || dropIndex === -1) {
        return;
      }

      const reorderedColumns = [...allColumns];
      const [draggedColumn] = reorderedColumns.splice(draggedIndex, 1);
      reorderedColumns.splice(dropIndex, 0, draggedColumn);

      const updatedOrder = reorderedColumns.reduce((acc, [key], index) => {
        acc[key] = index + 1;
        return acc;
      }, {} as { [key: string]: number });

      onReorder(updatedOrder);
    }
    setDraggedItem(null);
    setDragOverItem(null);
  };

  return (
    <Modal
      title="Column Settings"
      open={isVisible}
      onCancel={onCancel}
      footer={[
        <Button key="reset" onClick={onReset}>
          Reset to Default
        </Button>,
        <Button key="close" type="primary" onClick={onCancel}>
          Close
        </Button>
      ]}
      bodyStyle={{ 
        maxHeight: '60vh',
        overflow: 'hidden',
        padding: 0 
      }}
      width={600}
    > 
      <div 
        className="pe-2 py-4 column-settings-modal-content" 
        style={{ 
          maxHeight: 'calc(60vh - 40px)',
          overflowY: 'auto',
          overflowX: 'hidden'
        }}
      >
        <div className="mb-4 text-sm text-gray-600 px-4">
          Drag and drop to reorder columns. Toggle visibility with the switches.
        </div>
        <div className="space-y-2 px-4">
          {sortedColumns.map(([key, value]) => (
            <div
              key={key}
              className={clsx(
                'column-settings-item flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50',
                dragOverItem === key && 'drag-over',
                draggedItem === key && 'dragging'
              )}
              draggable={true}
              onDragStart={(e) => handleDragStart(e, key)}
              onDragEnter={(e) => handleDragEnter(e, key)}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDragEnd={handleDragEnd}
            >
              <div className="flex items-center gap-3 cursor-move">
                <VerticalAlignMiddleOutlined className="text-primary move-icon" />
                <span className="font-medium capitalize">
                  {columnDisplayNames[key] || key.replace(/_/g, ' ')}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={requiredColumns.includes(key) ? true : value}
                  onChange={() => onToggle(key)}
                  size="small"
                  disabled={requiredColumns.includes(key)}
                  className={clsx('column-settings-switch', value && 'bg-blue-500')}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
});

type CitySortKey = 'city' | 'companyCount' | 'conversionRate' | 'topPerformerValue';
type CompanySortKey = 'company' | 'salesRep' | 'stage' | 'stageValue';

interface CityTopPerformer {
  company: string;
  salesRep: string;
  value: number;
  stageLabel: string;
  stageKey: SalesPipelineStage;
  stageValueLabel: string;
}

interface CityGroup {
  city: string;
  rows: PipelineDrawerRow[];
  companyCount: number;
  conversionCount: number;
  conversionRate: number;
  stageDistribution: Partial<Record<SalesPipelineStage, number>>;
  topPerformer: CityTopPerformer | null;
}

interface SalesPipelineLegendProps {
  pipelineStageFilter: SalesPipelineStage | 'ALL';
  onToggleStage: (stage: SalesPipelineStage | 'ALL') => void;
  pipelineSummary: PipelineSummary;
  pipelineStageSequence: SalesPipelineStage[];
  pipelineStageColors: Record<SalesPipelineStage, { fill: string; text: string; shortLabel: string }>;
  formatShortCurrency: (value: number) => string;
}

export const SalesPipelineLegend: React.FC<SalesPipelineLegendProps> = ({
  pipelineStageFilter,
  onToggleStage,
  pipelineSummary,
  pipelineStageSequence,
  pipelineStageColors,
  formatShortCurrency,
}) => {
  const totalPipelineValue = pipelineStageSequence.reduce<number>(
    (sum, stage) => sum + Number(pipelineSummary.stageMap?.[stage]?.value ?? 0),
    0,
  );

  return (
    <div className="fixed bottom-24 left-4 z-[1000] flex flex-col items-start gap-3">
      <div className="flex flex-wrap items-center gap-2 rounded-full border border-slate-200/70 bg-white/90 px-4 py-2 shadow-2xl backdrop-blur">
        <AntButton
          type={pipelineStageFilter === 'ALL' ? 'primary' : 'default'}
          shape="round"
          onClick={() => onToggleStage('ALL')}
          className={clsx(
            '!flex !items-center !gap-2 !border !px-4 !py-1.5 !text-xs !font-semibold',
            pipelineStageFilter === 'ALL'
              ? '!border-slate-900 !bg-slate-900 !text-white shadow-lg'
              : '!border-slate-200/70 !bg-white !text-slate-900 hover:!bg-slate-100',
          )}
        >
          <span>All</span>
          <span className="text-sm font-bold">{pipelineSummary.totalClients.toLocaleString()}</span>
          <span
            className={clsx(
              'text-[11px] font-semibold',
              pipelineStageFilter === 'ALL' ? 'text-slate-200' : 'text-slate-500',
            )}
          >
            ({formatShortCurrency(totalPipelineValue)})
          </span>
        </AntButton>
        {pipelineStageSequence.map((stage) => {
          const stageStats = pipelineSummary.stageMap?.[stage] ?? { count: 0, value: 0 };
          const config = pipelineStageColors[stage];
          const isActive = pipelineStageFilter === stage;
          const stageValueNumeric = Number(stageStats.value ?? 0);
          const showValue = stage !== 'ENQUIRY' && stage !== 'OPPORTUNITY' && stageValueNumeric > 0;
          const stageLabel = config.shortLabel || stage.toLowerCase().replace('_', ' ');
          const valueLabel =
            stageValueNumeric === 0
              ? '$0'
              : `$${formatShortCurrency(stageValueNumeric) || Math.round(stageValueNumeric).toLocaleString()}`;

          return (
            <AntButton
              key={stage}
              shape="round"
              onClick={() => onToggleStage(stage)}
              className={clsx(
                '!flex !items-center !gap-2 !border !px-3 !py-1.5 !text-xs !font-semibold !transition',
                isActive
                  ? '!border-transparent !text-white shadow-lg'
                  : '!border-slate-200/70 !bg-white !text-slate-900 hover:!bg-slate-100',
              )}
              style={
                isActive
                  ? {
                      background: config.fill,
                      color: config.text,
                    }
                  : undefined
              }
            >
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ background: config.fill, boxShadow: '0 0 0 2px rgba(15,23,42,0.08)' }}
              />
              <span className="font-semibold capitalize">{stageLabel}</span>
              <span className={clsx('text-sm font-bold', isActive ? 'text-white' : 'text-slate-700')}>
                {stageStats.count.toLocaleString()}
              </span>
              {showValue && (
                <span
                  className={clsx(
                    'text-[10px] font-semibold uppercase tracking-wide',
                    isActive ? 'text-white/80' : 'text-slate-500',
                  )}
                >
                  {valueLabel}
                </span>
              )}
            </AntButton>
          );
        })}
      </div>
    </div>
  );
};

interface SalesPipelineDrawerContentProps {
  hasEntries: boolean;
  aggregates: PipelineDrawerAggregates;
  pipelineSummary: PipelineSummary;
  drawerContext: 'overall' | 'subset';
  metricsOverride?: {
    stageCounts: Record<SalesPipelineStage, number>;
    stageValues: Record<SalesPipelineStage, number>;
    totalCount: number;
    totalValue: number;
  } | null;
  rows: PipelineDrawerRow[];
  formatCurrency: (value: number) => string;
  formatPipelineCount: (value?: number) => string;
  hexToRgba: (hex: string, alpha: number) => string;
}

const companyPageSizeOptions = [10, 20, 50, 100];

const formatPercentage = (value: number): string => `${value.toFixed(1)}%`;

const getStageUrl = (stage: SalesPipelineStage, company: string): string => {
  const base = env.SALES_URL || 'https://sales.dbi360.com';
  const suffix = STAGE_URL_SUFFIX[stage] ?? '';
  return `${base}${suffix}${encodeURIComponent(company)}`;
};

const normalizeSearchText = (value: string): string => value.trim().toLowerCase();

const citySortComparators: Record<CitySortKey, (a: CityGroup, b: CityGroup) => number> = {
  city: (a, b) => a.city.localeCompare(b.city, undefined, { sensitivity: 'base' }),
  companyCount: (a, b) => a.companyCount - b.companyCount,
  conversionRate: (a, b) => a.conversionRate - b.conversionRate,
  topPerformerValue: (a, b) => (a.topPerformer?.value ?? 0) - (b.topPerformer?.value ?? 0),
};

const companySortComparators: Record<CompanySortKey, (a: PipelineDrawerRow, b: PipelineDrawerRow) => number> = {
  company: (a, b) => a.company.localeCompare(b.company, undefined, { sensitivity: 'base' }),
  salesRep: (a, b) => a.salesRep.localeCompare(b.salesRep, undefined, { sensitivity: 'base' }),
  stage: (a, b) => a.stageLabel.localeCompare(b.stageLabel, undefined, { sensitivity: 'base' }),
  stageValue: (a, b) => a.stageValueRaw - b.stageValueRaw,
};

const getEntityCompanyUrl = (row: PipelineDrawerRow): string => {
  const base = env.ENTITY_URL || 'https://entity.dbi360.com';
  if (row.clientId) {
    return `${base}/clients/${row.clientId}`;
  }
  const encoded = encodeURIComponent(row.company || '');
  return `${base}/clients?search=${encoded}`;
};

export const SalesPipelineDrawerContent: React.FC<SalesPipelineDrawerContentProps> = ({
  hasEntries,
  aggregates,
  pipelineSummary,
  drawerContext,
  metricsOverride,
  rows,
  formatCurrency,
  formatPipelineCount,
  hexToRgba,
}) => {
  const [citySearch, setCitySearch] = React.useState('');
  const [cityPage, setCityPage] = React.useState(1);
  const [cityPageSize, setCityPageSize] = React.useState(CITY_ROWS_PER_PAGE);

  const [companyDrawerOpen, setCompanyDrawerOpen] = React.useState(false);
  const [activeCity, setActiveCity] = React.useState<CityGroup | null>(null);
  const [companySearch, setCompanySearch] = React.useState('');
  const [companyPage, setCompanyPage] = React.useState(1);
  const [companyPageSize, setCompanyPageSize] = React.useState<number>(COMPANY_ROWS_PER_PAGE);

  // City table column settings state
  const [isCitySettingsModalVisible, setIsCitySettingsModalVisible] = React.useState(false);
  const [cityColumnVisibility, setCityColumnVisibility] = React.useState<Record<string, boolean>>({
    city: true,
    companyCount: true,
    conversionRate: true,
    topPerformerValue: true,
    stageDistribution: true,
  });
  const [cityColumnOrder, setCityColumnOrder] = React.useState<Record<string, number>>({});

  // Company table column settings state
  const [isCompanySettingsModalVisible, setIsCompanySettingsModalVisible] = React.useState(false);
  const [companyColumnVisibility, setCompanyColumnVisibility] = React.useState<Record<string, boolean>>({
    company: true,
    salesRep: true,
    stageStats: true,
  });
  const [companyColumnOrder, setCompanyColumnOrder] = React.useState<Record<string, number>>({});

  // Initialize city column order
  React.useEffect(() => {
    if (Object.keys(cityColumnOrder).length === 0) {
      const defaultOrder: Record<string, number> = {
        city: 1,
        companyCount: 2,
        conversionRate: 3,
        topPerformerValue: 4,
        stageDistribution: 5,
      };
      setCityColumnOrder(defaultOrder);
    }
  }, [cityColumnOrder]);

  // Initialize company column order
  React.useEffect(() => {
    if (Object.keys(companyColumnOrder).length === 0) {
      const defaultOrder: Record<string, number> = {
        company: 1,
        salesRep: 2,
        stageStats: 3,
      };
      setCompanyColumnOrder(defaultOrder);
    }
  }, [companyColumnOrder]);

  const handleCityColumnToggle = (key: string) => {
    if (key === 'city') return; // City is required
    setCityColumnVisibility((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleResetCityColumns = () => {
    const defaultOrder: Record<string, number> = {
      city: 1,
      companyCount: 2,
      conversionRate: 3,
      topPerformerValue: 4,
      stageDistribution: 5,
    };
    setCityColumnOrder(defaultOrder);
    setCityColumnVisibility({
      city: true,
      companyCount: true,
      conversionRate: true,
      topPerformerValue: true,
      stageDistribution: true,
    });
  };

  const handleReorderCityColumns = (updatedOrder: Record<string, number>) => {
    setCityColumnOrder(updatedOrder);
  };

  const handleCompanyColumnToggle = (key: string) => {
    setCompanyColumnVisibility((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleResetCompanyColumns = () => {
    const defaultOrder: Record<string, number> = {
      company: 1,
      salesRep: 2,
      stageStats: 3,
    };
    setCompanyColumnOrder(defaultOrder);
    setCompanyColumnVisibility({
      company: true,
      salesRep: true,
      stageStats: true,
    });
  };

  const handleReorderCompanyColumns = (updatedOrder: Record<string, number>) => {
    setCompanyColumnOrder(updatedOrder);
  };

  const cityColumnDisplayNames: Record<string, string> = {
    city: 'City',
    companyCount: 'Companies',
    conversionRate: 'Conversion Rate',
    topPerformerValue: 'Top Performer',
    stageDistribution: 'Stage Mix',
  };

  const companyColumnDisplayNames: Record<string, string> = {
    company: 'Company',
    salesRep: 'Sales Rep',
    stageStats: 'Pipeline Stages',
  };

  const cityGroups = React.useMemo<CityGroup[]>(() => {
    if (!rows.length) {
      return [];
    }

    const map = new Map<string, CityGroup>();

    rows.forEach((row) => {
      const cityName = row.city?.trim() || 'Not specified';
      const group = map.get(cityName) ?? {
        city: cityName,
        rows: [],
        companyCount: 0,
        conversionCount: 0,
        conversionRate: 0,
        stageDistribution: {},
        topPerformer: null,
      };

      const invoiceValue = Number.isFinite(row.invoiceValueRaw) ? row.invoiceValueRaw : 0;
      const invoiceStat = row.stageStats.find((stat) => stat.stage === 'INVOICED');
      const invoiceLabel = invoiceStat?.value ?? formatCurrency(invoiceValue);
      const salesRepName = row.salesRep?.trim().length ? row.salesRep : 'Unassigned';

      group.rows.push(row);
      group.companyCount += 1;
      group.stageDistribution[row.stageKey] = (group.stageDistribution[row.stageKey] ?? 0) + 1;
      if (row.stageKey === 'INVOICED') {
        group.conversionCount += 1;
      }

      const shouldReplaceTopPerformer = !group.topPerformer || invoiceValue > group.topPerformer.value;

      if (shouldReplaceTopPerformer) {
        group.topPerformer = {
          company: row.company,
          salesRep: salesRepName,
          value: invoiceValue,
          stageLabel: 'invoiced',
          stageKey: 'INVOICED',
          stageValueLabel: invoiceLabel,
        };
      }

      group.conversionRate =
        group.companyCount > 0 ? (group.conversionCount / group.companyCount) * 100 : 0;

      map.set(cityName, group);
    });

    return Array.from(map.values());
  }, [rows, formatCurrency]);

  const filteredCityGroups = React.useMemo(() => {
    if (!citySearch.trim()) {
      return cityGroups;
    }
    const value = normalizeSearchText(citySearch);
    return cityGroups.filter((group) => {
      const topPerformerMatches =
        group.topPerformer &&
        (group.topPerformer.company.toLowerCase().includes(value) ||
          group.topPerformer.salesRep.toLowerCase().includes(value));
      return (
        group.city.toLowerCase().includes(value) ||
        topPerformerMatches ||
        group.rows.some((row) => row.company.toLowerCase().includes(value))
      );
    });
  }, [cityGroups, citySearch]);

  React.useEffect(() => {
    setCityPage(1);
  }, [citySearch, rows.length]);

  const openCompanyDrawer = (group: CityGroup) => {
    setActiveCity(group);
    setCompanyDrawerOpen(true);
    setCompanySearch('');
    setCompanyPage(1);
    setCompanyPageSize(COMPANY_ROWS_PER_PAGE);
  };

  const closeCompanyDrawer = () => {
    setCompanyDrawerOpen(false);
    setActiveCity(null);
  };

  const activeCompanyRows = activeCity?.rows ?? [];

  const filteredCompanyRows = React.useMemo(() => {
    if (!companySearch.trim()) {
      return activeCompanyRows;
    }
    const value = normalizeSearchText(companySearch);
    return activeCompanyRows.filter((row) => {
      return (
        row.company.toLowerCase().includes(value) ||
        row.salesRep.toLowerCase().includes(value) ||
        row.stageLabel.toLowerCase().includes(value)
      );
    });
  }, [activeCompanyRows, companySearch]);

  React.useEffect(() => {
    setCompanyPage(1);
  }, [companySearch, companyPageSize, companyDrawerOpen]);

  const metricCards = React.useMemo(() => {
    const totalClientsRaw = pipelineSummary?.totalClients;
    const totalClients =
      typeof totalClientsRaw === 'number' && Number.isFinite(totalClientsRaw) ? totalClientsRaw : 0;
    const aggregateClientsRaw = aggregates?.uniqueClientCount;
    const aggregateClients =
      typeof aggregateClientsRaw === 'number' && Number.isFinite(aggregateClientsRaw) ? aggregateClientsRaw : 0;

    const hasAggregateContext = hasEntries && rows.length > 0;
    const hasSummaryTotals = totalClients > 0;
    const hasAggregateTotals = aggregateClients > 0;
    const representsSubset =
      hasAggregateTotals && (!hasSummaryTotals || aggregateClients < totalClients);
    const useAggregates = drawerContext === 'subset' && hasAggregateContext && representsSubset;
    const shouldUseOverride = drawerContext === 'subset' && Boolean(metricsOverride);

    return PIPELINE_STAGE_SEQUENCE.map((stage) => {
      const summary = pipelineSummary?.stageMap?.[stage];
      const aggregateCountRaw = aggregates.stageCounts?.[stage];
      const aggregateValueRaw = aggregates.stageValues?.[stage];
      const summaryCount = summary?.count;
      const summaryValue = summary?.value;

      const aggregateCount =
        typeof aggregateCountRaw === 'number' && Number.isFinite(aggregateCountRaw) ? aggregateCountRaw : 0;
      const aggregateValue =
        typeof aggregateValueRaw === 'number' && Number.isFinite(aggregateValueRaw) ? aggregateValueRaw : 0;
      const normalizedSummaryCount =
        typeof summaryCount === 'number' && Number.isFinite(summaryCount) ? summaryCount : 0;
      const normalizedSummaryValue =
        typeof summaryValue === 'number' && Number.isFinite(summaryValue) ? summaryValue : 0;

      const overrideCount = metricsOverride?.stageCounts?.[stage];
      const overrideValue = metricsOverride?.stageValues?.[stage];

      const primaryValue = shouldUseOverride
        ? (typeof overrideCount === 'number' && Number.isFinite(overrideCount) ? overrideCount : normalizedSummaryCount)
        : useAggregates
          ? aggregateCount
          : normalizedSummaryCount;
      const secondaryValue = shouldUseOverride
        ? (typeof overrideValue === 'number' && Number.isFinite(overrideValue) ? overrideValue : normalizedSummaryValue)
        : useAggregates
          ? aggregateValue
          : normalizedSummaryValue;

      return {
        stage,
        title: STAGE_CARD_LABELS[stage],
        primaryValue,
        secondaryValue,
        background: STAGE_CARD_BACKGROUNDS[stage],
        accent: STAGE_COLORS[stage],
      };
    });
  }, [aggregates, drawerContext, hasEntries, metricsOverride, pipelineSummary, rows.length]);

  // City Table Columns - with settings
  const allCityColumns: TableColumnsType<CityGroup> = React.useMemo(() => [
    {
      title: 'City',
      dataIndex: 'city',
      key: 'city',
      sorter: (a, b) => citySortComparators.city(a, b),
      render: (city: string) => <span className="font-semibold">{city}</span>,
    },
    {
      title: 'Companies',
      dataIndex: 'companyCount',
      key: 'companyCount',
      align: 'center',
      sorter: (a, b) => citySortComparators.companyCount(a, b),
      defaultSortOrder: 'descend',
      render: (count: number) => (
        <span className="font-bold text-blue-600">{formatPipelineCount(count)}</span>
      ),
    },
    {
      title: 'Conversion Rate',
      dataIndex: 'conversionRate',
      key: 'conversionRate',
      align: 'center',
      sorter: (a, b) => citySortComparators.conversionRate(a, b),
      render: (rate: number, record: CityGroup) => (
        <div className="flex flex-col items-center">
          <span className={clsx('font-semibold', rate > 35 ? 'text-green-600' : 'text-yellow-600')}>
            {formatPercentage(rate)}
          </span>
          <span className="text-xs text-gray-500">
            {formatPipelineCount(record.conversionCount)} converted
          </span>
        </div>
      ),
    },
    {
      title: 'Top Performer',
      dataIndex: 'topPerformer',
      key: 'topPerformerValue',
      sorter: (a, b) => citySortComparators.topPerformerValue(a, b),
      render: (topPerformer: CityTopPerformer | null) => {
        if (!topPerformer) {
          return <span className="text-sm text-gray-500">No active deals</span>;
        }
        return (
          <div className="flex flex-col gap-0.5">
            <Tooltip title={topPerformer.company} placement="top">
              <span className="font-semibold">{topPerformer.salesRep || topPerformer.company}</span>
            </Tooltip>
            <span className="text-xs font-semibold text-gray-900">
              Invoiced {topPerformer.stageValueLabel}
            </span>
          </div>
        );
      },
    },
    {
      title: (
        <div className="flex justify-between">
          Stage Mix
          <Tooltip title="Column Setting">
            <Button
              type="text"
              icon={<SettingOutlined />}
              onClick={() => setIsCitySettingsModalVisible(true)}
              size="small"
            />
          </Tooltip>
        </div>
      ),
      dataIndex: 'stageDistribution',
      key: 'stageDistribution',
      render: (stageDistribution: Partial<Record<SalesPipelineStage, number>>, record: CityGroup) => (
        <Space wrap size={[4, 4]}>
          {PIPELINE_STAGE_SEQUENCE.map((stage) => {
            const count = stageDistribution[stage] ?? 0;
            if (!count) return null;
            return (
              <Tag
                key={`${record.city}-${stage}`}
                style={{
                  backgroundColor: hexToRgba(STAGE_COLORS[stage], 0.12),
                  color: STAGE_COLORS[stage],
                  borderColor: hexToRgba(STAGE_COLORS[stage], 0.4),
                  fontWeight: 600,
                }}
              >
                {stage.replace('_', ' ')} - {formatPipelineCount(count)}
              </Tag>
            );
          })}
        </Space>
      ),
    },
  ], [formatPipelineCount, hexToRgba]);

  // Filter and sort city columns based on visibility and order
  const cityColumns: TableColumnsType<CityGroup> = React.useMemo(() => {
    return allCityColumns
      .filter((col) => cityColumnVisibility[col.key as string])
      .sort((a, b) => {
        const orderA = cityColumnOrder[a.key as string] || 999;
        const orderB = cityColumnOrder[b.key as string] || 999;
        return orderA - orderB;
      });
  }, [allCityColumns, cityColumnVisibility, cityColumnOrder]);

  // Company Table Columns - with settings
  const allCompanyColumns: TableColumnsType<PipelineDrawerRow> = React.useMemo(() => [
    {
      title: 'Company',
      dataIndex: 'company',
      key: 'company',
      sorter: (a, b) => companySortComparators.company(a, b),
      render: (company: string, row: PipelineDrawerRow) => (
        <div className="flex flex-col gap-0.5">
          <a
            href={getEntityCompanyUrl(row)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => row.clientId && handleEntityLinkClick(e, row.clientId)}
            className="font-bold text-green-600 hover:underline cursor-pointer"
          >
            {company}
          </a>
          {row.location && <span className="text-xs text-gray-500">{row.location}</span>}
        </div>
      ),
    },
    {
      title: 'Sales Rep',
      dataIndex: 'salesRep',
      key: 'salesRep',
      sorter: (a, b) => companySortComparators.salesRep(a, b),
      render: (salesRep: string) => (
        <span className="font-medium">{salesRep || 'Unassigned'}</span>
      ),
    },
    {
      title: (
        <div className='flex justify-between'>
          Pipeline Stages
          <Tooltip title="Column Setting">
            <Button
              type="text"
              icon={<SettingOutlined />}
              onClick={() => setIsCompanySettingsModalVisible(true)}
              size="small"
            />
          </Tooltip>
        </div>
      ),
      key: 'stageStats',
      render: (_: any, row: PipelineDrawerRow) => (
        <Space size={[12, 12]} wrap className="flex items-center">
          {row.stageStats.map((stat, index) => {
            const stageColor = STAGE_COLORS[stat.stage] ?? '#0f172a';
            const isActive = stat.isActive;
            const backgroundColor = isActive ? hexToRgba(stageColor, 0.16) : '#f8fafc';
            const border = isActive ? `2px solid ${stageColor}` : '1px solid #d8e3f2';
            const boxShadow = isActive ? `0 10px 22px ${hexToRgba(stageColor, 0.22)}` : 'none';
            const labelColor = isActive ? stageColor : '#64748b';
            const countColor = isActive ? '#0f172a' : '#1f2937';
            const valueColor = isActive ? stageColor : '#94a3b8';
            const dotColor = isActive ? stageColor : '#cbd5e1';
            const linkProps = isActive
              ? {
                  href: getStageUrl(stat.stage, row.company),
                  target: '_blank' as const,
                  rel: 'noopener noreferrer',
                }
              : {};
            
            const stageBox = (
              <div
                {...(isActive ? { onClick: (e: React.MouseEvent) => e.stopPropagation() } : {})}
                className={clsx(
                  'flex flex-col items-center justify-center gap-1.5 min-w-[120px] px-4 py-3.5 rounded-lg transition-all duration-200',
                  isActive && 'cursor-pointer hover:-translate-y-0.5'
                )}
                style={{
                  backgroundColor,
                  border,
                  boxShadow,
                  ...(isActive && linkProps.href ? { cursor: 'pointer' } : { cursor: 'default' }),
                }}
              >
                <div className="flex items-center gap-1.5">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: dotColor }}
                  />
                  <span
                    className="text-[10px] font-bold tracking-wider uppercase"
                    style={{ color: labelColor }}
                  >
                    {stat.label}
                  </span>
                </div>
                <span className="text-lg font-extrabold" style={{ color: countColor }}>
                  {stat.count}
                </span>
                {stat.value && (
                  <span className="text-sm font-semibold" style={{ color: valueColor }}>
                    {stat.value}
                  </span>
                )}
              </div>
            );

            return (
              <React.Fragment key={`${row.key}-${stat.stage}`}>
                <Tooltip title={stat.tooltip ?? ''} placement="top">
                  {isActive && linkProps.href ? (
                    <a {...linkProps} className="no-underline">
                      {stageBox}
                    </a>
                  ) : (
                    stageBox
                  )}
                </Tooltip>
                {index < row.stageStats.length - 1 && (
                  <ArrowRightOutlined className="text-base text-slate-300" />
                )}
              </React.Fragment>
            );
          })}
        </Space>
      ),
    },
  ], [hexToRgba]);

  // Filter and sort company columns based on visibility and order
  const companyColumns: TableColumnsType<PipelineDrawerRow> = React.useMemo(() => {
    return allCompanyColumns
      .filter((col) => companyColumnVisibility[col.key as string])
      .sort((a, b) => {
        const orderA = companyColumnOrder[a.key as string] || 999;
        const orderB = companyColumnOrder[b.key as string] || 999;
        return orderA - orderB;
      });
  }, [allCompanyColumns, companyColumnVisibility, companyColumnOrder]);

  if (!rows.length) {
    return (
      <div className="min-h-[280px] flex flex-col items-center justify-center gap-4 py-12">
        {!hasEntries ? (
          <>
            <Spin size="large" />
            <Text type="secondary">Fetching pipeline data&hellip;</Text>
          </>
        ) : (
          <Text type="secondary">No pipeline information available for the current filters.</Text>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {metricCards.map((card) => {
          const hasSecondary = Number(card.secondaryValue) > 0;
          return (
            <div
              key={card.stage}
              className="p-3 rounded-lg shadow-lg"
              style={{
                background: card.background,
                border: `1px solid ${hexToRgba(card.accent, 0.25)}`,
              }}
            >
              <Text
                strong
                className="text-[10px] font-bold tracking-wider uppercase block"
                style={{ color: card.accent }}
              >
                {card.title}
              </Text>
              <Title level={4} className="!mb-0 !mt-2 font-extrabold text-gray-900">
                {formatPipelineCount(card.primaryValue)}
              </Title>
              {hasSecondary && (
                <Text strong className="text-sm block mt-1" style={{ color: card.accent }}>
                  ({formatCurrency(Number(card.secondaryValue))})
                </Text>
              )}
            </div>
          );
        })}
      </div>

      {/* City Performance Table */}
      <div className="overflow-hidden">
        <Title level={5} className="font-bold">
          City Performance
        </Title>
        <Input
          size='large'
          placeholder="Search city, company or sales rep"
          suffix={<SearchOutlined className="text-lg !text-blue-600" />}
          value={citySearch}
          onChange={(e) => setCitySearch(e.target.value)}
          className="w-full sm:!w-96 !rounded mb-4"
          allowClear
        />

        <Table
          columns={cityColumns}
          dataSource={filteredCityGroups}
          rowKey="city"
          size="small"
          className='border'
          pagination={{
            current: cityPage,
            pageSize: cityPageSize,
            total: filteredCityGroups.length,
            onChange: (page, pageSize) => {
              setCityPage(page);
              setCityPageSize(pageSize || CITY_ROWS_PER_PAGE);
            },
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            className: '!px-4',
          }}
          onRow={(record) => ({
            onClick: () => openCompanyDrawer(record),
            className: 'cursor-pointer hover:bg-gray-50',
          })}
          locale={{
            emptyText: (
              <div className="py-8 text-center">
                <Text type="secondary">No city matches the current filters.</Text>
              </div>
            ),
          }}
        />
      </div>

      {/* Company Drawer */}
      <Drawer
        title={
          <div className="flex flex-col gap-1">
            {activeCity?.city ?? 'City'}
            <Text type="secondary" className="text-sm">
              {formatPipelineCount(activeCity?.companyCount ?? 0)} companies • {formatPercentage(activeCity?.conversionRate ?? 0)} conversion
            </Text>
          </div>
        }
        placement="right"
        width={1200}
        onClose={closeCompanyDrawer}
        open={companyDrawerOpen}
        closeIcon={<CloseOutlined />}
      >
        <div className="flex flex-col gap-4 h-full">
          <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-center">
            <Input
              size='large'
              placeholder="Search company, rep or stage"
              suffix={<SearchOutlined className="text-lg !text-blue-600" />}
              value={companySearch}
              onChange={(e) => setCompanySearch(e.target.value)}
              className="w-full sm:!w-96 !rounded"
              allowClear
            />
            {/* <Select
              value={companyPageSize}
              onChange={setCompanyPageSize}
              className="w-full md:w-32"
              options={companyPageSizeOptions.map((size) => ({
                label: `Show ${size}`,
                value: size,
              }))}
            /> */}
          </div>

            <Table
              columns={companyColumns}
              dataSource={filteredCompanyRows}
              rowKey="key"
              size="small"
              scroll={{ x: 'max-content' }}
              className='border'
              pagination={{
                current: companyPage,
                pageSize: companyPageSize,
                total: filteredCompanyRows.length,
                onChange: (page, pageSize) => {
                  setCompanyPage(page);
                  setCompanyPageSize(pageSize || COMPANY_ROWS_PER_PAGE);
                },
                showSizeChanger: true,
                pageSizeOptions: companyPageSizeOptions,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                className: '!px-4',
              }}
              locale={{
                emptyText: (
                  <div className="py-8 text-center">
                    <Text type="secondary">No companies found for the selected filters.</Text>
                  </div>
                ),
              }}
            />
        </div>
      </Drawer>

      {/* City Column Settings Modal */}
      <ColumnSettingsModal
        isVisible={isCitySettingsModalVisible}
        onCancel={() => setIsCitySettingsModalVisible(false)}
        columns={cityColumnVisibility}
        columnOrder={cityColumnOrder}
        onToggle={handleCityColumnToggle}
        onReset={handleResetCityColumns}
        onReorder={handleReorderCityColumns}
        requiredColumns={['city', 'stageDistribution']}
        columnDisplayNames={cityColumnDisplayNames}
      />

      {/* Company Column Settings Modal */}
      <ColumnSettingsModal
        isVisible={isCompanySettingsModalVisible}
        onCancel={() => setIsCompanySettingsModalVisible(false)}
        columns={companyColumnVisibility}
        columnOrder={companyColumnOrder}
        onToggle={handleCompanyColumnToggle}
        onReset={handleResetCompanyColumns}
        onReorder={handleReorderCompanyColumns}
        requiredColumns={['stageStats']}
        columnDisplayNames={companyColumnDisplayNames}
      />
    </div>
  );
};
